/**
 * RetailVitamin Pro v1.0 - Main JavaScript
 * Vanilla ES6+, No jQuery
 * Contract: Shopify AJAX API, local-first posture
 * Performance: < 30KB uncompressed
 */

(function() {
  'use strict';

  /* ==========================================================================
     1. INITIALIZATION
     ========================================================================== */

  document.addEventListener('DOMContentLoaded', function() {
    initCart();
    initKeyboardNavigation();
    initLazyLoading();
    initSmoothScroll();

    console.log('RetailVitamin Pro v1.0 initialized');
  });

  /* ==========================================================================
     2. CART MANAGEMENT (AJAX)
     ========================================================================== */

  // Maximum time (ms) to wait for a /cart/add.js response before aborting.
  // Prevents the ATC button from staying disabled on a stalled connection.
  var ADD_TO_CART_TIMEOUT_MS = 10000;

  // Maximum time (ms) to wait for updateCartDrawer() to populate the DOM before
  // opening the drawer anyway.  Keeps the drawer responsive on stalled networks
  // while still providing enough time for typical CDN round-trips (~200–400 ms).
  var DRAWER_OPEN_SAFETY_TIMEOUT_MS = 1500;

  // Guards for /cart.js count fetches. Newer requests always win so stale
  // responses cannot overwrite a fresher cart count during rapid cart changes.
  var _cartCountFetchId = 0;
  var _cartCountAbortController = null;

  /**
   * Initialize cart functionality
   */
  function initCart() {
    updateCartCount();

    document.addEventListener('click', function(event) {
      const button = event.target.closest('[data-action="add-to-cart"]');
      if (!button) return;

      if (button.dataset.stopPropagation === 'true') {
        event.stopPropagation();
      }

      window.addToCart(event, button);
    });
  }

  /**
   * Add item to cart (AJAX)
   * @param {Event} event - Click event
   * @param {HTMLElement} button - Add to cart button
   */
  window.addToCart = function(event, button) {
    event.preventDefault();

    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    const form = button.closest('form');
    const quantityInput = form ? form.querySelector('[name="quantity"]') : null;
    const requestedQuantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
    const quantity = Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? requestedQuantity : 1;
    const feedbackEl = productId ? document.getElementById(`cart-feedback-${productId}`) : null;
    const defaultLabel = button.dataset.defaultLabel || button.textContent.trim();

    // Disable button
    button.disabled = true;
    button.textContent = 'Adding...';

    // 10-second timeout guard: prevents the button staying disabled forever
    // if the network stalls.  AbortController is widely supported (ES2017+).
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), ADD_TO_CART_TIMEOUT_MS);

    // AJAX request to Shopify Cart API
    fetch('/cart/add.js', {
      method: 'POST',
      signal: abortController.signal,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: quantity,
          properties: {
            '_batch_tracking': 'pending',
            '_product_id': productId
          }
        }]
      })
    })
    .then(response => {
      clearTimeout(timeoutId);
      // Shopify Challenge (bot-detection): re-enable the button before navigating
      // so the UI is clean if the user hits Back after solving the challenge.
      if (response.redirected && response.url.includes('/challenge')) {
        clearTimeout(timeoutId);
        button.disabled = false;
        button.textContent = 'Add to Cart';
        window.location.href = response.url;
        // Return a never-resolving promise: halt the chain while navigation completes.
        return new Promise(() => {});
      }
      if (response.status === 429) {
        return Promise.reject(new Error('rate_limited'));
      }
      if (response.status === 422) {
        return Promise.reject(new Error('unavailable'));
      }
      if (!response.ok) throw new Error('Cart add failed');
      return response.json();
    })
    .then(data => {
      // Success: update count badge (fast /cart.js path, independent of section render).
      updateCartCount();

      if (feedbackEl) {
        feedbackEl.classList.remove('d-none', 'alert-danger');
        feedbackEl.classList.add('alert-success');
        feedbackEl.textContent = `✓ Added ${quantity} to cart`;
        setTimeout(() => feedbackEl.classList.add('d-none'), 3000);
      }

      // Signal a successful add so premium-feature listeners can respond
      // (e.g. toast, analytics) without racing against the async drawer render.
      document.dispatchEvent(new CustomEvent('rv:add-to-cart-success'));

      // Re-enable button early so it is interactive while the drawer loads.
      button.disabled = false;
      button.textContent = defaultLabel;

      // Show the cart drawer, pre-filling its contents before the open animation
      // begins so the user never sees the "Your cart is empty" empty-state flash.
      //
      // Strategy:
      //   1. If the drawer is already open, shown.bs.offcanvas will NOT fire;
      //      call updateCartDrawer() directly so items refresh.
      //   2. If the drawer is closed, call updateCartDrawer() first, then open
      //      the drawer only after the DOM has been populated (Promise chain).
      //      A 1500 ms safety timeout ensures the drawer still opens on stalled
      //      networks rather than leaving the user with a broken button.
      //   3. Use getOrCreateInstance (not `new`) to avoid stacking multiple
      //      Bootstrap Offcanvas instances on the same element.
      const cartDrawerEl = document.getElementById('cartDrawer');
      if (cartDrawerEl && typeof bootstrap !== 'undefined') {
        const cartDrawer = bootstrap.Offcanvas.getOrCreateInstance(cartDrawerEl);
        const alreadyOpen = cartDrawerEl.classList.contains('show');

        if (alreadyOpen) {
          // Drawer is visible: shown.bs.offcanvas will not fire, refresh manually.
          if (window.updateCartDrawer) {
            window.updateCartDrawer();
          }
        } else {
          // Drawer is closed: populate items first, then open.
          const openDrawer = function() { cartDrawer.show(); };

          if (window.updateCartDrawer) {
            // Signal to the shown.bs.offcanvas handler that this open was already
            // preceded by an updateCartDrawer() call so it must not refresh again.
            window._rvSkipNextOpenRefresh = true;
            const drawerOpenTimeout = setTimeout(openDrawer, DRAWER_OPEN_SAFETY_TIMEOUT_MS);
            const p = window.updateCartDrawer();
            // Check for a thenable (Promise-like) return value; avoids an
            // `instanceof Promise` check that fails across iframes or polyfills.
            if (p && typeof p.then === 'function') {
              p.then(
                function() { clearTimeout(drawerOpenTimeout); openDrawer(); },
                function() { clearTimeout(drawerOpenTimeout); openDrawer(); }
              );
            } else {
              clearTimeout(drawerOpenTimeout);
              openDrawer();
            }
          } else {
            openDrawer();
          }
        }
      }

      // Screen reader announcement
      announceToScreenReader(`Added to cart successfully`);
    })
    .catch(error => {
      clearTimeout(timeoutId);
      button.disabled = false;
      if (feedbackEl) {
        feedbackEl.classList.remove('d-none', 'alert-success');
        feedbackEl.classList.add('alert-danger');
      }
      if (error.name === 'AbortError') {
        button.textContent = 'Timed Out — Try Again';
        if (feedbackEl) feedbackEl.textContent = 'Request timed out. Please try again.';
        announceToScreenReader('Request timed out. Please try again.');
        return;
      }
      if (error.message === 'rate_limited') {
        button.textContent = 'Too Many Requests — Wait & Retry';
        if (feedbackEl) feedbackEl.textContent = 'Too many requests. Please wait a moment and try again.';
        announceToScreenReader('Too many requests. Please wait a moment and try again.');
        return;
      }
      if (error.message === 'unavailable') {
        button.textContent = 'Item Unavailable';
        if (feedbackEl) feedbackEl.textContent = 'This item is currently unavailable.';
        announceToScreenReader('This item is currently unavailable.');
        return;
      }
      console.error('Cart add error:', error);
      button.textContent = 'Error - Try Again';
      if (feedbackEl) feedbackEl.textContent = 'Error adding to cart. Please try again.';
      announceToScreenReader('Error adding to cart. Please try again.');
    });
  };

  /**
   * Update cart count badge
   */
  window.updateCartCount = function() {
    var fetchId = ++_cartCountFetchId;

    if (_cartCountAbortController) {
      _cartCountAbortController.abort();
    }
    _cartCountAbortController = new AbortController();

    return fetch('/cart.js', { signal: _cartCountAbortController.signal, cache: 'no-store' })
      .then(response => response.json())
      .then(cart => {
        if (fetchId !== _cartCountFetchId) return;

        const itemCount = Number.isFinite(cart.item_count) ? cart.item_count : 0;

        document.querySelectorAll('.cart-count, [data-cart-count]').forEach(el => {
          el.textContent = itemCount;
        });

        document.querySelectorAll('[data-cart-count-text]').forEach(el => {
          el.textContent = `Cart (${itemCount})`;
        });

        document.querySelectorAll('[data-cart-toggle]').forEach(el => {
          el.setAttribute('aria-label', `Open shopping cart. ${itemCount} items in cart`);
        });

        document.querySelectorAll('[data-cart-toggle]').forEach(toggle => {
          let badge = toggle.querySelector('[data-cart-badge]');

          if (itemCount > 0) {
            if (!badge) {
              badge = document.createElement('span');
              badge.setAttribute('data-cart-badge', '');
              badge.setAttribute('data-cart-count', '');
              badge.setAttribute('aria-hidden', 'true');
              badge.className = 'position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger';
              toggle.appendChild(badge);
            }
            badge.textContent = itemCount;
          } else if (badge) {
            badge.remove();
          }
        });

        document.dispatchEvent(new CustomEvent('rv:cart-count-updated', {
          detail: { itemCount: itemCount }
        }));
      })
      .catch(error => {
        if (error && error.name === 'AbortError') return;
        console.error('Cart count update error:', error);
      })
      .finally(() => {
        if (fetchId === _cartCountFetchId) {
          _cartCountAbortController = null;
        }
      });
  };

  /* ==========================================================================
     3. KEYBOARD NAVIGATION (ACCESSIBILITY)
     ========================================================================== */

  function initKeyboardNavigation() {
    // ESC key closes modals and offcanvas
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        // Close Bootstrap modals
        document.querySelectorAll('.modal.show').forEach(modal => {
          if (typeof bootstrap !== 'undefined') {
            const bsModal = bootstrap.Modal.getInstance(modal);
            if (bsModal) bsModal.hide();
          }
        });

        // Close Bootstrap offcanvas
        document.querySelectorAll('.offcanvas.show').forEach(offcanvas => {
          if (typeof bootstrap !== 'undefined') {
            const bsOffcanvas = bootstrap.Offcanvas.getInstance(offcanvas);
            if (bsOffcanvas) bsOffcanvas.hide();
          }
        });
      }
    });

    // Focus trap for modals (basic implementation)
    const focusableSelectors = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled])';

    document.querySelectorAll('.modal, .offcanvas').forEach(container => {
      container.addEventListener('keydown', function(e) {
        if (e.key === 'Tab') {
          const focusableElements = container.querySelectorAll(focusableSelectors);
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      });
    });
  }

  /* ==========================================================================
     4. LAZY LOADING (PERFORMANCE)
     ========================================================================== */

  function initLazyLoading() {
    // Use native lazy loading if supported
    if ('loading' in HTMLImageElement.prototype) {
      const images = document.querySelectorAll('img[data-src]');
      images.forEach(img => {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      });
    } else {
      // Fallback: Intersection Observer
      if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              img.src = img.dataset.src;
              img.classList.add('lazyloaded');
              img.classList.remove('lazyload');
              observer.unobserve(img);
            }
          });
        });

        document.querySelectorAll('img[data-src]').forEach(img => {
          imageObserver.observe(img);
        });
      }
    }
  }

  /* ==========================================================================
     5. SMOOTH SCROLL (UX ENHANCEMENT)
     ========================================================================== */

  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;

        const targetElement = document.querySelector(targetId);
        if (targetElement) {
          e.preventDefault();
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });

          // Update focus for accessibility
          targetElement.setAttribute('tabindex', '-1');
          targetElement.focus();
        }
      });
    });
  }

  /* ==========================================================================
     7. SCREEN READER ANNOUNCEMENTS (ACCESSIBILITY)
     ========================================================================== */

  /**
   * Announce message to screen readers
   * @param {string} message - Message to announce
   */
  function announceToScreenReader(message) {
    const liveRegion = document.getElementById('sr-live-region') || createLiveRegion();
    liveRegion.textContent = message;

    // Clear after 1 second
    setTimeout(() => {
      liveRegion.textContent = '';
    }, 1000);
  }

  /**
   * Create ARIA live region for screen reader announcements
   */
  function createLiveRegion() {
    const liveRegion = document.createElement('div');
    liveRegion.id = 'sr-live-region';
    liveRegion.className = 'sr-only';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    document.body.appendChild(liveRegion);
    return liveRegion;
  }

  /* ==========================================================================
     8. UTILITY FUNCTIONS
     ========================================================================== */

  /**
   * Debounce function for performance
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in ms
   */
  window.debounce = function(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  /**
   * Throttle function for performance
   * @param {Function} func - Function to throttle
   * @param {number} limit - Time limit in ms
   */
  window.throttle = function(func, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  };

  /* ==========================================================================
     9. SHOPIFY MONEY FORMATTING (POLYFILL)
     ========================================================================== */

  if (typeof Shopify === 'undefined') {
    window.Shopify = {};
  }

  /**
   * Format price with currency
   * @param {number} cents - Price in cents
   * @param {string} format - Money format string
   */
  Shopify.formatMoney = function(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');

    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision || 2;
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) return '0';

      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? (decimal + parts[1]) : '';

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  };

})();
